import { toast } from "react-toastify";
import {
  JUST_NOW,
  TODAY,
  YESTERDAY, 
  AM,
  PM,
  AM_REPLACEMENT,
  PM_REPLACEMENT,
  TOAST_ERROR_CREATING_CHANNEL,
  TOAST_CHANNEL_NOT_FOUND,
  TOAST_CONVERSATION_DELETED,
  TOAST_FAILED_TO_DELETE_CONVERSATION,
  TOAST_MESSAGE_DELETED,
  TOAST_FAILED_TO_DELETE_MESSAGE,
  TOAST_FAILED_TO_BLOCK_USER,
} from "../constants";
import { useAuthStore } from "../store/useAuthStore"; // Import the auth store
import { TIMEZONE_ABBREVIATION_OVERRIDES } from "../constants/Appointment";
import { APPOINTMENT_ACTION_PREFIXES, APPOINTMENT_ACTION_TYPES, APPOINTMENT_STATUS } from "../constants/Messages";
function isYesterday(d) {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    d.getDate() === yesterday.getDate() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getFullYear() === yesterday.getFullYear()
  );
}

export const formatDaysAgo = (dateString) => {
    const date = new Date(dateString);
    let time = date.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
    time = time
        .replace(AM, AM_REPLACEMENT)
        .replace(PM, PM_REPLACEMENT)
        .replace(AM.toLowerCase(), AM_REPLACEMENT)
        .replace(PM.toLowerCase(), PM_REPLACEMENT);
    return time;
};

export function formatTimestamp(dateInput) {
    const date = new Date(dateInput);
    const now = new Date();
    const diffSeconds = Math.floor((now - date) / 1000);

    // Helper for AM/PM replacement
    function formatTime(d) {
        let time = d.toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
        return time
            .replace(AM, AM_REPLACEMENT)
            .replace(PM, PM_REPLACEMENT)
            .replace(AM.toLowerCase(), AM_REPLACEMENT)
            .replace(PM.toLowerCase(), PM_REPLACEMENT);
    }

    // 1. Just now: < 5s
    if (diffSeconds < 5) {
        return JUST_NOW;
    }
    // 2. 1 minute ago: < 60s
    if (diffSeconds < 60) {
        return "1 minute ago";
    }
    // 3. X minutes ago: < 60min
    if (diffSeconds < 3600) {
        const minutes = Math.floor(diffSeconds / 60);
        return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    }
    // 4. 1 hour ago: < 2h
    if (diffSeconds < 7200) {
        return "1 hour ago";
    }
    // 5. X hours ago: < 24h
    if (diffSeconds < 86400) {
        const hours = Math.floor(diffSeconds / 3600);
        return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    }
    // 6. Yesterday at 5:05 p.m.
    if (isYesterday(date)) {
        return `Yesterday at ${formatTime(date)}`;
    }
    // 7. X days ago at 5:05 p.m.: < 7d
    if (diffSeconds < 604800) {
        const days = Math.floor(diffSeconds / 86400);
        return `${days} day${days === 1 ? "" : "s"} ago at ${formatTime(date)}`;
    }
    // 8. August 8 at 5:05 p.m.: this year, > 7d ago
    if (now.getFullYear() === date.getFullYear()) {
        return `${date.toLocaleString("default", { month: "long" })} ${date.getDate()} at ${formatTime(date)}`;
    }
    // 9. August 8, 2018 at 5:05 p.m.: past years
    return `${date.toLocaleString("default", { month: "long" })} ${date.getDate()}, ${date.getFullYear()} at ${formatTime(date)}`;
}

/**
 * Returns a date separator string for chat messages:
 * - "Today" if the date is today
 * - "Yesterday" if the date is yesterday
 * - Weekday name (e.g., "Monday") if within the current week
 * - Otherwise, a formatted date string (e.g., "August 8, 2018")
 * @param {string|Date} dateInput
 * @returns {string}
 */
export function formatMessageDateSeparator(dateInput) {
    const date = new Date(dateInput);
    const now = new Date();
    const calendar = {
        isDateInToday: (d) => d.toDateString() === now.toDateString(),
        isDateInYesterday: (d) => {
            const yesterday = new Date(now);
            yesterday.setDate(now.getDate() - 1);
            return d.toDateString() === yesterday.toDateString();
        },
        isDateInThisWeek: (d) => {
            // Get the start of the week (Sunday)
            const startOfWeek = new Date(now);
            startOfWeek.setDate(now.getDate() - now.getDay());
            startOfWeek.setHours(0, 0, 0, 0);
            // Get the end of the week (Saturday)
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);
            endOfWeek.setHours(23, 59, 59, 999);
            return d >= startOfWeek && d <= endOfWeek;
        }
    };

    if (calendar.isDateInToday(date)) {
        return TODAY;
    } else if (calendar.isDateInYesterday(date)) {
        return YESTERDAY
    } else if (calendar.isDateInThisWeek(date)) {
        // Return weekday name (e.g., "Monday")
        return date.toLocaleDateString(undefined, { weekday: "long" });
    } else {
        // Return formatted date (e.g., "August 8, 2018")
        return date.toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric"
        });
    }
}
/**
 * Loads more messages for the active channel.
 * @param {Object} params
 * @param {Object} params.activeChannel - The currently active channel.
 * @param {boolean} params.loadingMore - Is loading more messages.
 * @param {boolean} params.hasMore - Are there more messages to load.
 * @param {Function} params.setLoadingMore - Setter for loadingMore state.
 * @param {Function} params.setHasMore - Setter for hasMore state.
 */
export const loadMoreMessages = async ({
  activeChannel,
  loadingMore,
  hasMore,
  setLoadingMore,
  setHasMore,
}) => {
  if (!activeChannel || loadingMore || !hasMore) return;

  setLoadingMore(true);
  const oldestMessage = activeChannel.state.messages[0];

  if (!oldestMessage) {
    setLoadingMore(false);
    return;
  }

  const response = await activeChannel.query({
    messages: {
      limit: 50,
      id_lt: oldestMessage.id,
    },
  });

  if (response.messages.length === 0) {
    setHasMore(false); // No more messages to load
  }

  setLoadingMore(false);
  // Mark messages as read when the user scrolls to the bottom
  if (activeChannel.state.isUpToDate) {
    await activeChannel.markRead();
  }
};
/**
 * Fetches user details for given user IDs.
 * @param {Object} params
 * @param {Object} params.chatClient - The Stream chat client instance.
 * @param {Array<string>} params.userIdsToFetch - Array of user IDs to fetch.
 * @param {string} params.primaryUserId - The primary user ID (for mapping).
 * @param {Function} params.setUserDetails - Setter for userDetails state.
 * @param {Function} params.setChatError - Setter for chatError state.
 * @returns {Promise<{primaryUser: Object, secondaryUser: Object}>}
 */
export const fetchUserDetails = async ({
  chatClient,
  userIdsToFetch,
  primaryUserId,
  setUserDetails,
  setChatError,
}) => {
   if (!Array.isArray(userIdsToFetch) || userIdsToFetch.length === 0) {
        // Optionally, clear user details or set error if needed
        if (setUserDetails) {
            setUserDetails({ primary: null, secondary: null });
        }
        return {};
    }
  try {
    const response = await chatClient.queryUsers(
      { id: { $in: userIdsToFetch } },
      {},
      {}
    );

    const primaryUser = response.users.find((u) => u.id === primaryUserId);
    const secondaryUser = response.users.find(
      (u) => u.id === userIdsToFetch[1]
    );

    if (setUserDetails) {
      setUserDetails({
        primary: primaryUser || null,
        secondary: secondaryUser || null,
      });
    }

    return { primaryUser, secondaryUser };
  } catch (err) {
    if (setChatError) setChatError(err.message);
    return {};
  }
};
/**
 * Creates and initializes a new chat channel.
 * @param {Object} params
 * @param {Object} params.chatClient - The Stream chat client instance.
 * @param {string} params.primaryUserId - The primary user ID.
 * @param {string} params.secondaryUserId - The secondary user ID.
 * @param {string} params.team - The team name or ID.
 * @param {Function} params.setLoading - Setter for loading state.
 * @param {Function} params.onFetchUserDetails - Function to fetch user details.
 * @param {Function} params.setActiveChannel - Setter for activeChannel state.
 * @param {Function} params.setChatError - Setter for chatError state.
 */
export const createAndInitializeChannel = async ({
  chatClient,
  primaryUserId,
  secondaryUserId,
  team,
  setLoading,
  onFetchUserDetails,
  setActiveChannel,
  setChatError,
}) => {
  try {
    setLoading(true);
    const newChannel = chatClient.channel("messaging", {
      members: [primaryUserId, secondaryUserId],
      team,
    });

    await newChannel.create();
    await newChannel.watch({
      watch: true,
      state: true,
      preserve_history: true,
      limit: 50,
    });

    if (onFetchUserDetails) {
      await onFetchUserDetails([primaryUserId, secondaryUserId]);
    }

    chatClient.dispatchEvent({
      type: "channel.created.custom",
      channel: newChannel,
    });

    setActiveChannel(newChannel);
    setLoading(false);
  } catch (err) {
    if (setChatError) setChatError(err.message || "Error creating channel!");
    setLoading(false);
    toast.error(err.message || TOAST_ERROR_CREATING_CHANNEL, {
      toastId: "channel-added",
    });
  }
};
/**
 * hideAndDeleteChannelForUser a chat channel.
 * @param {Object} params
 * @param {Object} params.userOrChannel - The user or channel object to delete.
 * @param {Object} params.chatClient - The Stream chat client instance.
 * @param {Object} params.userIds - The user IDs object { primary, secondary }.
 * @param {Object} params.activeChannel - The currently active channel.
 * @param {Function} params.setActiveChannel - Setter for activeChannel state.
 * @param {Function} params.setUserIds - Setter for userIds state.
 */
export const hideAndDeleteChannelForUser = async ({
  userOrChannel,
  chatClient,
    primaryUserId,
  activeChannel,
  setActiveChannel,
    setSecondaryUserId,
}) => {
  try {
    let channelToHide = null;
    let deletedUserId = null;
    if (userOrChannel.channel) {
      channelToHide = userOrChannel.channel;
      // Find the other user in the channel
      const memberIds = Object.keys(channelToHide.state.members);
        deletedUserId = memberIds.find((id) => id !== primaryUserId);
    } else {
      const team = chatClient.user?.teams ? chatClient.user.teams[0] : "";
      const filters = {
        team: { $eq: team },
        type: "messaging",
          members: { $eq: [primaryUserId, userOrChannel.id] },
        member_count: { $eq: 2 },
      };
      const channels = await chatClient.queryChannels(
        filters,
        [{ last_message_at: -1 }],
        { limit: 1 }
      );
      channelToHide = channels[0];
      deletedUserId = userOrChannel.id;
    }

    if (!channelToHide) {
      toast.error(TOAST_CHANNEL_NOT_FOUND, { toastId: "conversation-deleted" });
      return;
    }
    // Hide the channel and clear its history for the current user
    await channelToHide.hide(null, true);
    toast.success(TOAST_CONVERSATION_DELETED, {
      toastId: "conversation-deleted",
    });
    if (activeChannel && activeChannel.id === channelToHide.id) {
        setActiveChannel(null);
        setSecondaryUserId(null);
    }
 // Check if accountId2 matches the deleted user and set it to null
 const { authData, setAuthData } = useAuthStore.getState();
 if (authData.accountId2 === parseInt(deletedUserId.split('_')[2])) {
   setAuthData({ ...authData, accountId2: null });
 }
    // Return both channel id and user id
    return { channelId: channelToHide.cid, userId: deletedUserId };
  } catch (err) {
    toast.error(TOAST_FAILED_TO_DELETE_CONVERSATION, {
      toastId: "conversation-deleted",
    });
  }
};

/**
 * Deletes a message from a channel.
 * @param {Object} params
 * @param {Object} params.chatClient - The Stream chat client instance.
 * @param {Object} params.activeChannel - The currently active channel.
 * @param {Object} params.messageToDelete - The message object to delete.
 * @param {Function} params.setIsDeleteModalOpen - Setter for delete modal state.
 * @param {Function} params.setMessageToDelete - Setter for messageToDelete state.
 */
export const handleDeleteMessage = async ({
  chatClient,
  activeChannel,
  messageToDelete, 
  setMessageToDelete,
}) => {
  if (!activeChannel || !messageToDelete) return;
  try {
    await chatClient.deleteMessage(messageToDelete.id);

    // Refresh all active channels to update their state (unread, last message, etc.)
    const activeChannels = chatClient.activeChannels
      ? Object.values(chatClient.activeChannels)
      : [];
    await Promise.all(
      activeChannels.map((channel) =>
        channel.watch({ watch: true, state: true, limit: 50 })
      )
    );
    toast.success(TOAST_MESSAGE_DELETED, { toastId: "delete-message" });
  } catch (err) {
    toast.error(TOAST_FAILED_TO_DELETE_MESSAGE, { toastId: "delete-message" });
  }  
  setMessageToDelete(null);
};

/**
 * Blocks a user for the current user in Stream Chat.
 * @param {Object} params
 * @param {Object} params.chatClient - The Stream chat client instance.
 * @param {string} params.userIdToBlock - The user ID to block.
 */
export const blockUser = async ({ chatClient, userIdToBlock }) => {
  try {
    await chatClient.blockUser(userIdToBlock);   
  } catch (err) {
    toast.error(TOAST_FAILED_TO_BLOCK_USER, { toastId: "block-message" });
  }
};

/**
 * Filters channels to only those with non-empty messageSets[0].messages arrays.
 * @param {Array} channels
 * @returns {Array}
 */
export function getChannelsWithMessages(channels) {
  return channels.filter(
    channel =>
      Array.isArray(channel.state?.messageSets?.[0]?.messages) &&
      channel.state.messageSets[0].messages.length > 0
  );
}
/**
 * Filters users to only those with non-null lastMessageDate values.
 * @param {Array} users
 * @returns {Array}
 */
export function filterUsersWithLastMessage(users) {
  return Array.isArray(users)
    ? users.filter(u => u.lastMessageDate !== null)
    : [];
}

/**
 * Parses a date string as midnight in the browser's local timezone.
 *
 * JavaScript's `new Date("2026-03-16")` treats date-only ISO strings as UTC,
 * which shifts them to the previous day in negative-offset timezones.
 * Appending `T00:00:00` forces local interpretation, keeping the calendar date correct.
 *
 * NOTE: The returned Date is for **display purposes only** (e.g. toLocaleDateString).
 * For expiration checks use {@link isAppointmentExpired}, which compares wall-clock
 * times in the event timezone.
 *
 * @param {string|Date} dateInput - A date string (e.g. "2026-03-16") or Date object.
 * @returns {Date|null}
 */
export function parseDateForDisplay(dateInput) {
  if (!dateInput) return null;
  if (dateInput instanceof Date) return new Date(dateInput);

  if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    return new Date(dateInput + 'T00:00:00');
  }
  return new Date(dateInput);
}

/**
 * Strips the leading zero from a single-digit hour (e.g. "09:00 AM" -> "9:00 AM").
 *
 * @param {string} [timeStr] - Time string such as "09:00 AM".
 * @returns {string|undefined}
 */
export function formatAppointmentTime(timeStr) {
  if (typeof timeStr !== 'string') return timeStr;
  return timeStr.replace(/^0(\d)/, '$1');
}

/**
 * Normalizes any `HH:MM AM/PM` substrings inside a chat message so that a
 * single-digit hour has no leading zero (e.g. "02:30 AM" -> "2:30 AM"). Used
 * for action-message text written by the Flutter iframe, which still emits
 * two-digit hours.
 *
 * @param {string} [text]
 * @returns {string|undefined}
 */
export function formatAppointmentTimesInText(text) {
  if (typeof text !== 'string') return text;
  return text.replace(/\b0(\d:\d{2}\s*(?:AM|PM))\b/g, '$1');
}

/**
 * Checks whether an appointment has expired in the given event timezone.
 *
 * Compares the current wall-clock time (in the event timezone) against the
 * appointment date and optional end time using pure numeric comparison,
 * avoiding Date-object pitfalls when browser and event timezones differ.
 *
 * @param {string} dateStr - Appointment date in YYYY-MM-DD format.
 * @param {string} [startTime] - Start time string (e.g. "10:00 AM").
 * @param {string} [endTime] - End time string (e.g. "3:00 PM").
 * @param {string} [eventTimezone] - IANA timezone. Defaults to browser timezone.
 * @returns {boolean}
 */
export function isAppointmentExpired(dateStr, startTime, endTime, eventTimezone) {
  if (!dateStr) return false;

  const tz = eventTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Current date/time parts in the event timezone
  const nowParts = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
  })
    .formatToParts(new Date())
    .forEach(({ type, value }) => { nowParts[type] = value; });

  const [appYear, appMonth, appDay] = dateStr.split('-').map(Number);
  const nowYear   = parseInt(nowParts.year, 10);
  const nowMonth  = parseInt(nowParts.month, 10);
  const nowDay    = parseInt(nowParts.day, 10);
  const nowHour   = parseInt(nowParts.hour, 10) % 24;
  const nowMinute = parseInt(nowParts.minute, 10);

  if (endTime) {
    const parseTp = (ts) => {
      if (!ts) return [0, 0];
      const [t, period] = ts.split(' ');
      let [h, m] = t.split(':').map(Number);
      if (period === 'PM' && h < 12) h += 12;
      else if (period === 'AM' && h === 12) h = 0;
      return [h, m || 0];
    };

    const [endH, endM] = parseTp(endTime);
    let endDay   = appDay;
    let endMonth = appMonth;
    let endYear  = appYear;

    // Handle appointments spanning midnight
    if (startTime) {
      const [startH, startM] = parseTp(startTime);
      if (endH < startH || (endH === startH && endM < startM)) {
        const next = new Date(appYear, appMonth - 1, appDay + 1);
        endYear  = next.getFullYear();
        endMonth = next.getMonth() + 1;
        endDay   = next.getDate();
      }
    }

    const nowVal = nowYear * 100000000 + nowMonth * 1000000 + nowDay  * 10000 + nowHour * 100 + nowMinute;
    const endVal = endYear * 100000000 + endMonth * 1000000 + endDay * 10000 + endH    * 100 + endM;
    return nowVal > endVal;
  }

  // Date-only: expired if today (in event TZ) is past the appointment date
  const nowDate = nowYear  * 10000 + nowMonth  * 100 + nowDay;
  const appDate = appYear  * 10000 + appMonth  * 100 + appDay;
  return appDate < nowDate;
}

/**
 * Returns a short timezone abbreviation for a given IANA timezone.
 * US timezones use Intl shortGeneric (ET, CT, PT, MT).
 * Non-US timezones fall back to TIMEZONE_ABBREVIATION_OVERRIDES.
 *
 * @param {string} [ianaZone] - IANA timezone (e.g. "America/New_York"). Defaults to browser timezone.
 * @returns {string|null}
 */
export function getShortTimezone(ianaZone) {
  const zone = ianaZone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (zone && TIMEZONE_ABBREVIATION_OVERRIDES[zone]) {
    return TIMEZONE_ABBREVIATION_OVERRIDES[zone];
  }

  try {
    // shortGeneric gives ET, CT, PT, MT for US zones and GMT for London.
    // For obscure zones it may return long names like "Ethiopia Time" —
    // fall back to short offset format (e.g. "GMT-5") in that case.
    const tz = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortGeneric' })
      .formatToParts(new Date())
      .find(p => p.type === 'timeZoneName')?.value || null;

    // If the result is longer than 5 characters (e.g. "American Samoa Time"),
    // use the compact GMT offset instead (e.g. "GMT-5")
    if (tz && tz.length > 5) {
      const offset = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
        .formatToParts(new Date())
        .find(p => p.type === 'timeZoneName')?.value || tz;
      return offset;
    }

    return tz;
  } catch {
    return null;
  }
}

export const isAppointmentActionMessage = (message) => {
    if (!message) return false;
    if (message?.appointment_action?.type === APPOINTMENT_STATUS.APPOINTMENT_ACTION) {
        return true;
    }

    const text = message?.text;
    if (!text) return false;
    return APPOINTMENT_ACTION_PREFIXES.some((prefix) =>
        APPOINTMENT_ACTION_TYPES.some((action) =>
            text.startsWith(prefix.replace("{action}", action))
        )
    );
};

/**
 * Whether an appointment is still open (requested or accepted), regardless of its time.
 *
 * @param {number} status - The appointment status.
 * @returns {boolean}
 */
export function isOpenAppointmentStatus(status) {
  return [APPOINTMENT_STATUS.REQUESTED, APPOINTMENT_STATUS.ACCEPTED].includes(status);
}

/**
 * Whether an appointment is closed (rejected, canceled or deleted).
 *
 * @param {number} status - The appointment status.
 * @returns {boolean}
 */
export function isClosedAppointmentStatus(status) {
  return [
    APPOINTMENT_STATUS.REJECTED,
    APPOINTMENT_STATUS.CANCELED,
    APPOINTMENT_STATUS.DELETED,
  ].includes(status);
}

/**
 * Whether an appointment can open the summary window: only a requested or accepted
 * appointment whose end time has not passed in the event timezone. Any other status,
 * including a missing or unknown one, cannot.
 *
 * @param {object} appointment - The message's `appointment` payload.
 * @param {string} [eventTimezone] - IANA timezone.
 * @returns {boolean}
 */
export function canOpenAppointmentSummary(appointment, eventTimezone) {
  if (!isOpenAppointmentStatus(appointment?.status)) return false;

  return !isAppointmentExpired(appointment.date, appointment.startTime, appointment.endTime, eventTimezone);
}

/**
 * Whether the My Schedule link can open the summary window: same as
 * {@link canOpenAppointmentSummary}, except an accepted appointment opens even after it
 * has ended, so it can be viewed (the summary disables its actions once it has expired).
 *
 * @param {object} appointment - The message's `appointment` payload.
 * @param {string} [eventTimezone] - IANA timezone.
 * @returns {boolean}
 */
export function canOpenAppointmentSummaryFromLink(appointment, eventTimezone) {
  return (
    appointment?.status === APPOINTMENT_STATUS.ACCEPTED ||
    canOpenAppointmentSummary(appointment, eventTimezone)
  );
}
